import type { BrainArea } from './records';
export const BRAIN_GAMES: {
    key: string;
    area: BrainArea;
    title: string;
    description: string;
    path: string;
}[] = [
    { key: 'memory_sequence', area: 'memory', title: 'brain.exercise.patternMatch', description: 'brain.desc.patternMatch', path: '/brain/memory-sequence' },
    { key: 'reverse_span', area: 'memory', title: 'brain.lab.reverse', description: 'brain.lab.reverseDesc', path: '/brain-training?lab=reverse_span' },
    { key: 'stroop_test', area: 'focus', title: 'brain.exercise.stroop', description: 'brain.desc.stroop', path: '/brain/stroop' },
    { key: 'rule_shift', area: 'focus', title: 'brain.lab.shift', description: 'brain.lab.shiftDesc', path: '/brain-training?lab=rule_shift' },
    { key: 'speed_reaction', area: 'speed', title: 'brain.exercise.numberFlow', description: 'brain.desc.numberFlow', path: '/brain/speed-reaction' },
    { key: 'mental_math', area: 'speed', title: 'brain.lab.math', description: 'brain.lab.mathDesc', path: '/brain-training?lab=mental_math' },
    { key: 'daily_brain', area: 'logic', title: 'brain.exercise.dailyBrain', description: 'brain.desc.dailyBrain', path: '/brain/daily-brain' },
    { key: 'number_pattern', area: 'logic', title: 'brain.lab.pattern', description: 'brain.lab.patternDesc', path: '/brain-training?lab=number_pattern' },
    { key: 'word_links', area: 'language', title: 'brain.exercise.wordLinks', description: 'brain.desc.wordLinks', path: '/brain/word-links' },
];
/** Variety first: untried activities, then least recently practised. No IQ inference. */
export function recommendBrainGame(last: Record<string, string>) {
    return [...BRAIN_GAMES].sort((a, b) => (Date.parse(last[a.key] ?? '') || 0) - (Date.parse(last[b.key] ?? '') || 0))[0];
}
export type LabGame = 'reverse_span' | 'rule_shift' | 'mental_math' | 'number_pattern';
export interface LabRound {
    cue: string;
    question: string;
    answer: string;
    choices: string[];
    explanation: string;
    preview?: string;
}
export function shuffle<T>(list: T[], random = Math.random) { const a = [...list]; for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
} return a; }
export function generateLabRound(game: LabGame, level: number, index: number, random = Math.random): LabRound {
    const lv = Math.max(1, Math.min(5, Math.round(level))), int = (min: number, max: number) => min + Math.floor(random() * (max - min + 1));
    const options = (n: number) => shuffle([String(n), String(n + 1), String(n - 1), String(n + int(2, 8))], random);
    if (game === 'reverse_span') {
        const digits = shuffle([1, 2, 3, 4, 5, 6, 7, 8, 9], random).slice(0, Math.min(7, lv + 2));
        const answer = [...digits].reverse().join(' '), pool = new Set([answer, digits.join(' ')]);
        // Bounded, deterministic alternatives; no loop depending on RNG uniqueness.
        pool.add([...digits.slice(1), digits[0]].join(' '));
        pool.add([digits[1], digits[0], ...digits.slice(2)].join(' '));
        return { cue: 'brain.lab.remember', question: 'brain.lab.reversePrompt', preview: digits.join(' '), answer, choices: shuffle([...pool], random), explanation: answer };
    }
    if (game === 'rule_shift') {
        const n = int(1, 9), even = lv < 3 ? index % 2 === 0 : random() < .5, answer = even ? (n % 2 === 0 ? 'brain.lab.even' : 'brain.lab.odd') : (n > 5 ? 'brain.lab.above' : 'brain.lab.below');
        // Exclude 5 from the magnitude task so neither answer is ambiguous.
        const value = !even && n === 5 ? 6 : n, correct = even ? answer : value > 5 ? 'brain.lab.above' : 'brain.lab.below';
        return { cue: even ? 'brain.lab.parity' : 'brain.lab.magnitude', question: String(value), answer: correct, choices: even ? ['brain.lab.even', 'brain.lab.odd'] : ['brain.lab.above', 'brain.lab.below'], explanation: correct };
    }
    if (game === 'mental_math') {
        const a = int(lv * 2, lv * 10), b = int(1, lv * 5), multiply = lv >= 3 && index % 2 === 1;
        const answer = multiply ? a * b : a + b;
        return { cue: 'brain.lab.calculate', question: `${a} ${multiply ? '×' : '+'} ${b}`, answer: String(answer), choices: options(answer), explanation: `${a} ${multiply ? '×' : '+'} ${b} = ${answer}` };
    }
    const start = int(1, 20), step = int(2, lv + 4), multiply = lv >= 3 && index % 2 === 1;
    const seq = Array.from({ length: 4 }, (_, i) => multiply ? start * (2 ** i) : start + step * i), answer = multiply ? seq[3] * 2 : seq[3] + step;
    return { cue: 'brain.lab.completePattern', question: seq.join(' · ') + ' · ?', answer: String(answer), choices: options(answer), explanation: seq.join(' → ') + ' → ' + answer };
}
